// src/features/checkout/CheckoutPage.jsx

import React, { useState } from "react";
import { useCart } from "../../hooks/useCart";
import { ordersService } from "../orders/ordersService";
import { paymentService } from "./paymentService";
import { formatCurrency } from "../../utils/formatCurrency";

const CheckoutPage = () => {
  const { cart: cartItems, clearCart, loading } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // User details form
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    address: "",
    city: "",
    zip: "",
    paymentMethod: "card", // default
  });

  // Payment method specific details
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: "",
    expiry: "",
    cvv: "",
    paypalEmail: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentDetailChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Validate all required fields
  const isFormValid = () => {
    // Basic user details
    const basicValid = Object.values(formData).every((val) => val.trim() !== "");
    if (!basicValid) return false;

    // Payment method specific validation
    if (formData.paymentMethod === "card") {
      return (
        paymentDetails.cardNumber.trim() !== "" &&
        paymentDetails.expiry.trim() !== "" &&
        paymentDetails.cvv.trim() !== ""
      );
    }
    if (formData.paymentMethod === "paypal") {
      return paymentDetails.paypalEmail.trim() !== "";
    }
    return true;
  };

  if (loading) {
    return <div className="text-center mt-10">Loading your cart...</div>;
  }

  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleCheckout = async () => {
    if (!isFormValid()) {
      setError("Please fill in all required fields.");
      return;
    }

    setCheckoutLoading(true);
    setError(null);

    try {
      // Prepare payment data including method-specific details
      const paymentData = {
        orderId: `temp_${Date.now()}`,
        amount: totalPrice,
        method: formData.paymentMethod,
        details:
          formData.paymentMethod === "card"
            ? {
                cardNumber: paymentDetails.cardNumber,
                expiry: paymentDetails.expiry,
                cvv: paymentDetails.cvv,
              }
            : {
                email: paymentDetails.paypalEmail,
              },
      };

      const paymentResult = await paymentService(paymentData);
      if (!paymentResult.success) throw new Error(paymentResult.message);

      // Prepare order data
      const orderData = {
        userId: null, // optionally add from auth if available
        items: cartItems,
        total: totalPrice,
        date: new Date().toISOString(),
        paymentId: paymentResult.transactionId,
        paymentMethod: formData.paymentMethod,
        customerName: formData.fullName,
        customerEmail: formData.email,
        customerAddress: formData.address,
        customerCity: formData.city,
        customerZip: formData.zip,
      };

      await ordersService.createOrder(orderData);

      clearCart();
      setSuccess("Order placed successfully!");
      // Optionally redirect to a thank you page or clear form
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return <p className="text-center mt-10">Your cart is empty.</p>;
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-2">
      <h1 className="text-3xl font-bold mb-6 text-center">Checkout</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column: User details + Payment method + dynamic fields */}
        <div className="bg-gray-50 p-4 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Your Details</h2>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP *</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold mb-2">Payment Method</h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={formData.paymentMethod === "card"}
                    onChange={handleInputChange}
                  />
                  <span>Credit / Debit Card</span>
                </label>
                {formData.paymentMethod === "card" && (
                  <div className="ml-6 space-y-2 mt-2">
                    <input
                      type="text"
                      name="cardNumber"
                      placeholder="Card Number"
                      value={paymentDetails.cardNumber}
                      onChange={handlePaymentDetailChange}
                      className="w-full border rounded px-3 py-2"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        name="expiry"
                        placeholder="MM/YY"
                        value={paymentDetails.expiry}
                        onChange={handlePaymentDetailChange}
                        className="border rounded px-3 py-2"
                      />
                      <input
                        type="text"
                        name="cvv"
                        placeholder="CVV"
                        value={paymentDetails.cvv}
                        onChange={handlePaymentDetailChange}
                        className="border rounded px-3 py-2"
                      />
                    </div>
                  </div>
                )}

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="paypal"
                    checked={formData.paymentMethod === "paypal"}
                    onChange={handleInputChange}
                  />
                  <span>PayPal</span>
                </label>
                {formData.paymentMethod === "paypal" && (
                  <div className="ml-6 mt-2">
                    <input
                      type="email"
                      name="paypalEmail"
                      placeholder="PayPal Email"
                      value={paymentDetails.paypalEmail}
                      onChange={handlePaymentDetailChange}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Right column: Order summary (unchanged) */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
          <div className="hidden md:block">
            <table className="w-full table-auto border-collapse border border-gray-300 mb-4">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border px-4 py-2">Item</th>
                  <th className="border px-4 py-2">Price</th>
                  <th className="border px-4 py-2">Qty</th>
                  <th className="border px-4 py-2">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((item) => (
                  <tr key={item.productId || item.id} className="text-center">
                    <td className="border px-4 py-2">{item.title || item.name}</td>
                    <td className="border px-4 py-2">{formatCurrency(item.price)}</td>
                    <td className="border px-4 py-2">{item.quantity}</td>
                    <td className="border px-4 py-2">
                      {formatCurrency(item.price * item.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3 mb-4">
            {cartItems.map((item) => (
              <div
                key={item.productId || item.id}
                className="border rounded-lg p-3 shadow flex flex-col space-y-1"
              >
                <div className="flex justify-between">
                  <span className="font-semibold">{item.title || item.name}</span>
                  <span>{formatCurrency(item.price)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Qty: {item.quantity}</span>
                  <span className="font-medium">
                    Subtotal: {formatCurrency(item.price * item.quantity)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mb-4 border-t pt-4">
            <h3 className="text-xl font-bold">Total</h3>
            <span className="text-xl font-bold">{formatCurrency(totalPrice)}</span>
          </div>
        </div>
      </div>

      {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
      {success && <p className="text-green-500 mb-4 text-center">{success}</p>}

      <button
        onClick={handleCheckout}
        disabled={checkoutLoading || !isFormValid()}
        className="w-full py-3 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 mt-4"
      >
        {checkoutLoading ? "Processing..." : "Place Order"}
      </button>
    </div>
  );
};

export default CheckoutPage;